import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { MapPin, Globe, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for geo-restriction state
const GeoContext = createContext({
  isAllowed: true,
  isLoading: true,
  country: null,
  countryCode: null,
  currency: 'USD',
  currencySymbol: '$',
  setGeoBlocked: () => {}
});

export const useGeo = () => useContext(GeoContext);

export const GeoProvider = ({ children }) => {
  const [geoState, setGeoState] = useState({
    isAllowed: true,
    isLoading: true,
    country: null,
    countryCode: null,
    currency: 'USD',
    currencySymbol: '$',
    message: ''
  });

  // Function to set geo blocked state (can be called from axios interceptor)
  const setGeoBlocked = useCallback((geoData) => {
    setGeoState(prev => ({
      ...prev,
      isAllowed: false,
      isLoading: false,
      country: geoData?.country || prev.country || 'Unknown',
      countryCode: geoData?.country_code || prev.countryCode || 'XX',
      message: geoData?.message || 'Sorry, NeporaForYou is currently only available in the United States and Canada.'
    }));
  }, []);

  useEffect(() => {
    checkGeoRestriction();
    
    // Set up axios interceptor to catch 403 geo_restricted errors
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 403 && 
            error.response?.data?.detail?.error === 'geo_restricted') {
          setGeoBlocked(error.response.data.detail);
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [setGeoBlocked]);

  const checkGeoRestriction = async () => {
    try {
      const response = await axios.get(`${API}/geo/check`);
      setGeoState({
        isAllowed: response.data.is_allowed,
        isLoading: false,
        country: response.data.country,
        countryCode: response.data.country_code,
        currency: response.data.currency || 'USD',
        currencySymbol: response.data.currency_symbol || '$',
        message: response.data.message
      });
    } catch (error) {
      console.error('Geo check failed:', error);
      // Fail open - allow access if check fails
      setGeoState({
        isAllowed: true,
        isLoading: false,
        country: 'Unknown',
        countryCode: 'XX',
        currency: 'USD',
        currencySymbol: '$',
        message: ''
      });
    }
  };

  return (
    <GeoContext.Provider value={{ ...geoState, setGeoBlocked }}>
      {children}
    </GeoContext.Provider>
  );
};

// Blocked page component shown to users from restricted countries
export const GeoBlockedPage = () => {
  const { country, message } = useGeo();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <Globe className="w-10 h-10 text-red-500" />
        </div>
        
        <h1 
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Not Available in Your Region
        </h1>
        
        <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
          <MapPin className="w-4 h-4" />
          <span>Detected Location: {country || 'Unknown'}</span>
        </div>
        
        <p className="text-gray-600 mb-6">
          {message || 'Sorry, NeporaForYou is currently only available in the United States and Canada.'}
        </p>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-left text-sm text-amber-800">
              <p className="font-medium mb-1">We're Expanding!</p>
              <p>We're working on bringing NeporaForYou to more countries soon. Please check back later!</p>
            </div>
          </div>
        </div>
        
        <div className="border-t pt-6">
          <p className="text-sm text-gray-500 mb-3">Currently serving:</p>
          <div className="flex justify-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇺🇸</span>
              <span className="text-sm font-medium">United States</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇨🇦</span>
              <span className="text-sm font-medium">Canada</span>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-xs text-gray-400">
          <p>Questions? Contact us at info@neporaforyou.com</p>
        </div>
      </div>
    </div>
  );
};

// Loading component while checking geo
export const GeoLoadingPage = () => {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
        <p className="text-gray-600">Checking availability in your region...</p>
      </div>
    </div>
  );
};

// Wrapper component that checks geo and shows appropriate content
export const GeoGate = ({ children }) => {
  const { isAllowed, isLoading } = useGeo();

  if (isLoading) {
    return <GeoLoadingPage />;
  }

  if (!isAllowed) {
    return <GeoBlockedPage />;
  }

  return children;
};

export default GeoProvider;
